import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

// Import template components
import IncidentDetailTemplate from './components/IncidentDetailTemplate';
import ProblemDetailTemplate from './components/ProblemDetailTemplate';
import ChangeDetailTemplate from './components/ChangeDetailTemplate';
import ServiceRequestDetailTemplate from './components/ServiceRequestDetailTemplate';
import CaseDetailTemplate from './components/CaseDetailTemplate';

export interface TicketData {
  id: string;
  number: string;
  ticketType: string;
  shortDescription: string;
  description: string;
  priority: string;
  state: string;
  problemState?: string;
  changeState?: string;
  category?: string;
  impact?: string;
  urgency?: string;
  createdAt: string;
  updatedAt: string;
  caller?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignedTo?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  AssignmentGroup?: {
    id: string;
    name: string;
    description?: string;
  };
  Customer?: {
    id: string;
    accountName: string;
    accountShortName?: string;
    emailAddress?: string;
  };
  Engagement?: {
    id: string;
    name: string;
    status: string;
  };
  Alert?: Array<{
    source?: string | null;
    sourceService?: string | null;
    sourceSubService?: string | null;
  }>;
  ServicePlan?: {
    id: string;
    name: string;
    type?: string;
  };
  Resource?: {
    id: string;
    name: string;
    type?: string;
    resourceId?: string;
    resourceGroupName?: string;
  };
  resourceIdValue?: string | null;
  node?: string | null;
  parent?: {
    id: string;
    number: string;
    shortDescription: string;
    ticketType: string;
  };
  workNotes?: any[];
  CaseActivity?: any[];
  // Type-specific fields
  resolution?: string;
  resolutionCode?: string;
  cause?: string;
  solution?: string;
  problemStatement?: string;
  reportedIssue?: string;
  actualServiceImpact?: string;
  briefAnalysis?: string;
  detailedAnalysis?: string;
  correctiveActions?: string;
  chronologyOfEvents?: string;
  problemDate?: string;
  rcaPresentedBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  assessment1Summary?: string;
  assessment2Summary?: string;
  rootCause?: string;
  workaround?: string;
  solutionImplemented?: boolean;
  implementationDate?: string;
  implementedDate?: string;
  changeType?: string;
  changeRisk?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
  plannedStart?: string;
  plannedEnd?: string;
  actualStart?: string;
  actualEnd?: string;
  implementationPlan?: string;
  implementationResults?: string;
  reviewResults?: string;
  closureNotes?: string;
  cancellationReason?: string;
  rollbackPlan?: string;
  backoutPlan?: string;
  testPlan?: string;
  justification?: string;
  riskImpactAnalysis?: string;
  channel?: string;
  contactType?: string;
  approvalGroup?: {
    id: string;
    name: string;
  };
  cabRequired?: boolean;
  customerApprovalRequested?: boolean;
  customerApprovalStatus?: string;
  customerApprovalComments?: string;
  contactId?: string;
  CustomerContact?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  User_Case_customerApprovedByIdToUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  /** SRE investigation gate status on Case (T-021 / T-022). */
  sreInvestigationStatus?: string | null;
  sreDecisionReason?: string | null;
  sreEvaluatedAt?: string | null;
  sreRunId?: string | null;
  sreDecisionId?: string | null;
  sreUseCaseId?: string | null;
}

const TicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { isContactUser } = useUserPermissions();
  const [activeTab, setActiveTab] = useState<'details' | 'approvals' | 'notes' | 'activity' | 'assessment' | 'resolution' | 'attachments'>('details');

  // Get the 'from' parameter to determine back navigation
  const fromPage = searchParams.get('from');

  // Get back navigation path and label based on 'from' parameter
  const getBackNavigation = () => {
    switch (fromPage) {
      case 'cases':
        return { path: '/cases', label: 'Back to Cases' };
      case 'dashboard':
        return { path: '/dashboard', label: 'Back to Dashboard' };
      default:
        return { path: '/tickets', label: 'Back to Tickets' };
    }
  };

  const backNav = getBackNavigation();

  const ticketsListSearch = (location.state as { ticketsListSearch?: string } | null)?.ticketsListSearch;

  const navigateBack = () => {
    if (fromPage === 'cases') {
      navigate('/cases');
      return;
    }
    if (fromPage === 'dashboard') {
      navigate('/dashboard');
      return;
    }
    if (ticketsListSearch !== undefined) {
      navigate(`/tickets${ticketsListSearch}`);
      return;
    }
    navigate('/tickets');
  };

  // Invalidate and refetch when ID changes to prevent stale data
  useEffect(() => {
    if (id) {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    }
  }, [id, queryClient]);

  // Fetch ticket data from unified endpoint
  const { data: ticket, isLoading, error, refetch } = useQuery({
    queryKey: ['ticket', id],
    queryFn: async () => {
      const response = await api.get(`/tickets/${id}`);
      return response.data as TicketData;
    },
    enabled: !!id,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0, // Don't cache data to prevent stale data issues
    retry: false // Don't retry failed requests to avoid confusion
  });

  // Determine if user is a Contact user and should have read-only access
  const isReadOnly = isContactUser;

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading ticket. Please try again.</p>
          <button
            onClick={() => navigate('/tickets')}
            className="mt-4 text-indigo-600 hover:text-indigo-900"
          >
            Back to Tickets
          </button>
        </div>
      </div>
    );
  }

  // Render the appropriate template based on ticket type
  const renderTicketTemplate = () => {
    switch (ticket.ticketType) {
      case 'INCIDENT':
        return (
          <IncidentDetailTemplate
            ticket={ticket}
            isReadOnly={isReadOnly}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onRefresh={refetch}
          />
        );
      case 'PROBLEM':
        return (
          <ProblemDetailTemplate
            ticket={ticket}
            isReadOnly={isReadOnly}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onRefresh={refetch}
          />
        );
      case 'CHANGE':
        return (
          <ChangeDetailTemplate
            ticket={ticket}
            isReadOnly={isReadOnly}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onRefresh={refetch}
          />
        );
      case 'SERVICE_REQUEST':
        return (
          <ServiceRequestDetailTemplate
            ticket={ticket}
            isReadOnly={isReadOnly}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onRefresh={refetch}
          />
        );
      case 'CASE':
        return (
          <CaseDetailTemplate
            ticket={ticket}
            isReadOnly={isReadOnly}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onRefresh={refetch}
          />
        );
      default:
        return (
          <div className="p-6">
            <p className="text-gray-500">Unknown ticket type: {ticket.ticketType}</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={navigateBack}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 flex items-center gap-1"
                title={backNav.label}
              >
                <ArrowLeftIcon className="h-5 w-5" />
                <span className="text-sm hidden sm:inline">{backNav.label}</span>
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {ticket.number}
                </h1>
                <p className="text-sm text-gray-500">
                  {ticket.ticketType.replace('_', ' ')} • View Mode {isReadOnly ? '(Read-Only)' : ''}
                </p>
              </div>
            </div>
            {isReadOnly && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md px-3 py-1">
                <p className="text-sm text-yellow-800">Read-Only Access</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Render the appropriate ticket template */}
      <div className="flex-1">
        {renderTicketTemplate()}
      </div>
    </div>
  );
};

export default TicketDetail;